// src/components/auth/ProtectedRoute.tsx - FIXED with proper types
import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../shared/LoadingSpinner';
import type { ProtectedRouteProps } from '../../types';

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowedRoles = [],
  requireUnauth = false,
  showRoleSwitchPrompt = true
}) => {
  const { isAuthenticated, user, loading, hasRole } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner />;
  }

  // If route requires unauthenticated user (like login/signup)
  if (requireUnauth) {
    if (isAuthenticated) {
      // Redirect to appropriate dashboard based on role
      if (user?.role === 'rider') {
        return <Navigate to="/dashboard/rider" state={{ from: location }} replace />;
      } else if (user?.role === 'customer') {
        return <Navigate to="/dashboard/customer" state={{ from: location }} replace />;
      } else if (user?.role === 'admin' || user?.role === 'support') {
        return <Navigate to="/admin" state={{ from: location }} replace />;
      } else {
        return <Navigate to="/choose-role" state={{ from: location }} replace />;
      }
    }
    return <>{children}</>;
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    // Save the attempted location for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check role-based access
  if (allowedRoles.length > 0) {
    const userRole = user?.role;
    
    if (!userRole || !allowedRoles.includes(userRole)) {
      // User doesn't have required role
      if (showRoleSwitchPrompt && userRole) {
        // If user has a role but not the right one, redirect to appropriate dashboard
        if (userRole === 'rider') {
          return <Navigate to="/dashboard/rider" state={{ from: location }} replace />;
        } else if (userRole === 'customer') {
          return <Navigate to="/dashboard/customer" state={{ from: location }} replace />;
        } else if (userRole === 'admin' || userRole === 'support') {
          return <Navigate to="/admin" state={{ from: location }} replace />;
        }
      }
      
      // Redirect to choose role if no role or unauthorized
      return <Navigate to="/choose-role" state={{ from: location, message: 'Please select a role to access this page' }} replace />;
    }
  }

  // User is authenticated and has required role
  return <>{children}</>;
};

export default ProtectedRoute;